import { createTheme, alpha } from '@mui/material/styles';

// Modern Color Palette
const palette = {
    primary: {
        main: '#2563eb', // Vibrant Blue
        light: '#60a5fa',
        dark: '#1d4ed8',
        contrastText: '#ffffff',
    },
    secondary: {
        main: '#10b981', // Fresh Green
        light: '#34d399',
        dark: '#059669',
        contrastText: '#ffffff',
    },
    background: {
        default: '#f8fafc', // Slate 50
        paper: '#ffffff',
    },
    text: {
        primary: '#1e293b', // Slate 800
        secondary: '#64748b', // Slate 500
    },
    divider: '#e2e8f0', // Slate 200
};

export const theme = createTheme({
    palette,
    typography: {
        fontFamily: [
            'Inter',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),
        h1: { fontWeight: 800, letterSpacing: '-0.025em' },
        h2: { fontWeight: 800, letterSpacing: '-0.025em' },
        h3: { fontWeight: 700, letterSpacing: '-0.025em' },
        h4: { fontWeight: 700, letterSpacing: '-0.025em' },
        h5: { fontWeight: 600, letterSpacing: '-0.025em' },
        h6: { fontWeight: 600 },
        button: { fontWeight: 600, textTransform: 'none' },
    },
    shape: {
        borderRadius: 16,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    scrollbarColor: '#94a3b8 #f1f5f9',
                    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                        backgroundColor: '#f1f5f9',
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                        borderRadius: 8,
                        backgroundColor: '#94a3b8',
                        minHeight: 24,
                        border: '2px solid #f1f5f9',
                    },
                },
                // Accessibility: Visible focus indicators for keyboard navigation
                '*:focus-visible': {
                    outline: `3px solid ${palette.primary.main}`,
                    outlineOffset: '2px',
                    borderRadius: '4px',
                },
                // Remove default focus outline for mouse users
                '*:focus:not(:focus-visible)': {
                    outline: 'none',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 50, // Pill shape
                    padding: '10px 24px',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                    // Enhanced focus indicator for buttons
                    '&:focus-visible': {
                        outline: `3px solid ${palette.primary.main}`,
                        outlineOffset: '3px',
                    },
                },
                containedPrimary: {
                    background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
                    '&:hover': {
                        background: `linear-gradient(135deg, ${palette.primary.dark} 0%, ${palette.primary.main} 100%)`,
                    },
                },
                containedSecondary: {
                    background: `linear-gradient(135deg, ${palette.secondary.main} 0%, ${palette.secondary.dark} 100%)`,
                    '&:hover': {
                        background: `linear-gradient(135deg, ${palette.secondary.dark} 0%, ${palette.secondary.main} 100%)`,
                    },
                },
                sizeLarge: {
                    padding: '14px 28px',
                    fontSize: '1.1rem',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 20,
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
                    border: '1px solid #f1f5f9',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.05), 0 4px 6px -4px rgb(0 0 0 / 0.05)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                elevation1: {
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.05), 0 1px 2px -1px rgb(0 0 0 / 0.05)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    background: alpha('#ffffff', 0.8),
                    backdropFilter: 'blur(12px)',
                    borderBottom: '1px solid',
                    borderColor: alpha('#e2e8f0', 0.8),
                    boxShadow: 'none',
                    color: palette.text.primary,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        backgroundColor: '#f8fafc',
                        '& fieldset': {
                            borderColor: '#e2e8f0',
                        },
                        '&:hover fieldset': {
                            borderColor: '#cbd5e1',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: palette.primary.main,
                            borderWidth: 2,
                        },
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 600,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    // Enhanced focus indicator for icon buttons
                    '&:focus-visible': {
                        outline: `3px solid ${palette.primary.main}`,
                        outlineOffset: '2px',
                    },
                },
            },
        },
    },
});
